import React from 'react';
import { BuildingOffice2Icon, ChevronRightIcon } from '@heroicons/react/24/outline';

interface Membership {
  tenant_id: number;
  tenant_name: string;
  tenant_slug: string;
  role: string;
  country?: string;
  default_currency?: string;
  last_active_at?: string;
}

interface WorkspaceSelectorProps {
  memberships: Membership[];
  onSelect: (tenantId: number) => void;
  loading?: boolean;
}

const ROLE_LABELS: Record<string, string> = {
  super_admin: 'Super Admin',
  project_manager: 'Project Manager',
  tenant_admin: 'Tenant Admin',
  business_manager: 'Business Manager',
  field_manager: 'Field Manager',
  field_scout: 'Field Scout',
  agronomist_advisor: 'Agronomist',
  agronomist: 'Agronomist',
  field_facilitator: 'Field Facilitator',
  finance_admin: 'Finance Admin',
  content_manager: 'Content Manager',
  viewer: 'Viewer',
};

const WorkspaceSelector: React.FC<WorkspaceSelectorProps> = ({
  memberships,
  onSelect,
  loading = false,
}) => {
  const formatLastActive = (date?: string) => {
    if (!date) return 'Never';
    const d = new Date(date);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    return d.toLocaleDateString();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-500 via-teal-600 to-cyan-700 p-4">
      <div className="w-full max-w-lg">
        <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl p-8 border border-white/20">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-cyan-600 rounded-2xl flex items-center justify-center shadow-xl">
                <BuildingOffice2Icon className="w-8 h-8 text-white" />
              </div>
            </div>
            <h1 className="text-2xl font-black text-slate-950">Choose Workspace</h1>
            <p className="mt-2 text-sm text-slate-500">
              You have access to multiple workspaces. Select one to continue.
            </p>
          </div>

          <div className="space-y-3">
            {memberships.map((m) => (
              <button
                key={m.tenant_id}
                onClick={() => onSelect(m.tenant_id)}
                disabled={loading}
                className="w-full flex items-center justify-between p-4 rounded-2xl border-2 border-slate-100 hover:border-emerald-500 hover:bg-emerald-50 transition-all text-left group disabled:opacity-50"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-100 to-cyan-100 flex items-center justify-center">
                    <span className="text-lg font-black text-emerald-700">
                      {m.tenant_name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="font-black text-slate-950">{m.tenant_name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
                        {ROLE_LABELS[m.role] || m.role}
                      </span>
                      {m.country && (
                        <span className="text-xs text-slate-500">{m.country}</span>
                      )}
                      {m.default_currency && (
                        <span className="text-xs text-slate-400">• {m.default_currency}</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-400 mt-1">
                      Last active: {formatLastActive(m.last_active_at)}
                    </p>
                  </div>
                </div>
                <ChevronRightIcon className="w-5 h-5 text-slate-300 group-hover:text-emerald-600 transition-colors" />
              </button>
            ))}
          </div>

          {memberships.length === 0 && (
            <div className="text-center py-8">
              <p className="text-sm text-slate-500">No workspaces available.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkspaceSelector;
